import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useToggleTenantStatus } from '../hooks/useToggleTenantStatus';
import type { AdminTenantListDto } from '../types/tenants.types';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Textarea } from '@/shared/components/ui/textarea';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

const schema = z.object({
  reason: z.string().min(3, 'Mínimo 3 caracteres.').max(500, 'Máximo 500 caracteres.'),
});

type FormValues = z.infer<typeof schema>;

interface ToggleTenantStatusModalProps {
  tenant: AdminTenantListDto;
  action: 'suspend' | 'reactivate';
  onClose: () => void;
}

export function ToggleTenantStatusModal({ tenant, action, onClose }: ToggleTenantStatusModalProps) {
  const { toggleStatus, isToggling } = useToggleTenantStatus();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
  });

  const handleClose = () => {
    if (!isToggling) {
      form.reset();
      onClose();
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await toggleStatus({ tenantId: tenant.id, reason: values.reason, action });
      const label = action === 'suspend' ? 'suspendido' : 'reactivado';
      Notification.success({
        title: 'Estado actualizado',
        description: `El espacio de trabajo ha sido ${label} exitosamente.`,
      });
      onClose();
    } catch {
      // global error handler shows the error; dialog stays open for retry
    }
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>
            {action === 'suspend' ? 'Suspender espacio de trabajo' : 'Reactivar espacio de trabajo'}
          </DialogTitle>
          <DialogDescription>
            {action === 'suspend'
              ? `Suspender ${tenant.name}. Se bloqueará el acceso a todos sus miembros.`
              : `Reactivar ${tenant.name}. Se restaurará el acceso a sus miembros.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Motivo</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={
                        action === 'suspend'
                          ? 'Ej. Violación de términos de servicio.'
                          : 'Ej. Problema resuelto, se restaura el acceso.'
                      }
                      rows={3}
                      disabled={isToggling}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <UnifiedButton
                type="button"
                buttonType="basic"
                variant="default"
                onClick={handleClose}
                disabled={isToggling}
              >
                Cancelar
              </UnifiedButton>
              <UnifiedButton
                type="submit"
                buttonType="basic"
                variant="outline"
                disabled={isToggling}
                actionType={'destructive'}
              >
                {isToggling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {action === 'suspend' ? 'Suspendiendo...' : 'Reactivando...'}
                  </>
                ) : action === 'suspend' ? (
                  'Suspender'
                ) : (
                  'Reactivar'
                )}
              </UnifiedButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
